import { UIManager, SWOElements } from "./modules/UIManager";
import { EditorManager } from "./modules/EditorManager";
import { PreviewManager } from "./modules/PreviewManager";
import { ConsoleBridge } from "./modules/ConsoleBridge";

export interface SWOOptions {
  /** Initial code when no saved draft exists (or with resetOnLoad). An empty string is preserved. */
  code?: string | null;
  /**
   * Stable localStorage key for draft persistence. Persistence is disabled
   * when no key is supplied (or inherited from `data-swo-storage-key`).
   * Previous versions generated a random key per page load, which could
   * never restore drafts; a stable key is now required by design.
   */
  storageKey?: string | null;
  /** Ignore any saved draft and start from `code` (or the demo). Default: false. */
  resetOnLoad?: boolean;
  /** Keep console output across preview refreshes. Default: false (console clears when the preview re-runs). */
  preserveConsole?: boolean;
  /** Base URL used to resolve relative assets in previews. Defaults to the host page's document base URI. */
  assetBaseURL?: string;
  layout?: "horizontal" | "vertical";
  layoutDirection?: "normal" | "reverse";
  headless?: boolean;
  primaryColor?: string;
}

// Augment HTMLElement to include swoInstance
declare global {
  interface HTMLElement {
    swoInstance?: SWO;
  }
}

class SWO {
  targetElement: HTMLElement;
  options: SWOOptions;

  ui: UIManager;
  editor: EditorManager;
  preview: PreviewManager;
  consoleBridge: ConsoleBridge;
  private _boundWindowMessageHandler: ((event: MessageEvent) => void) | null = null;
  private _destroyed: boolean = false;

  constructor(targetOrOptions?: string | HTMLElement | SWOOptions, optionsIfTarget?: SWOOptions) {
    let targetElement: HTMLElement | null = null;
    let mergedOptions: SWOOptions;

    if (
      typeof targetOrOptions === "string" ||
      targetOrOptions instanceof HTMLElement
    ) {
      targetElement =
        typeof targetOrOptions === "string"
          ? document.querySelector(targetOrOptions)
          : targetOrOptions;
      mergedOptions = optionsIfTarget || {};
    } else {
      mergedOptions = targetOrOptions || {};
      targetElement = document.querySelector("[data-swo]"); // Default target
    }

    if (!targetElement) {
      console.error(
        "SWO: Target element not found. Please provide a valid selector, DOM element, or ensure a [data-swo] element exists."
      );
      throw new Error("Target element not found");
    }
    this.targetElement = targetElement;

    // Enforce one live instance per target (F10). A previous live instance is
    // torn down first so its DOM, listeners, timers, and blob URLs cannot leak.
    const existingInstance = targetElement.swoInstance;
    if (existingInstance && !existingInstance.isDestroyed()) {
      existingInstance.destroy();
    }

    this.targetElement.classList.add("swo-container"); // Add base class for scoping

    // Validate and set options with proper type checking.
    // Empty-string code is intentional content, not a missing value (F08).
    const codeOption =
      mergedOptions.code !== undefined && mergedOptions.code !== null
        ? mergedOptions.code
        : this.targetElement.dataset.swoCode !== undefined
          ? this.targetElement.dataset.swoCode
          : null;

    // Persistence requires a stable key; without one, drafts are not saved (F07).
    const storageKey =
      mergedOptions.storageKey !== undefined && mergedOptions.storageKey !== null
        ? mergedOptions.storageKey
        : (this.targetElement.dataset.swoStorageKey ?? null);

    this.options = {
      code: codeOption,
      storageKey: storageKey,
      resetOnLoad: mergedOptions.resetOnLoad === true,
      preserveConsole: mergedOptions.preserveConsole === true,
      assetBaseURL: mergedOptions.assetBaseURL,
      layout: (mergedOptions.layout ?? this.targetElement.dataset.swoLayout) === "vertical" ? "vertical" : "horizontal",
      layoutDirection: (mergedOptions.layoutDirection ?? this.targetElement.dataset.swoLayoutDirection) === "reverse" ? "reverse" : "normal",
      headless: mergedOptions.headless ?? (this.targetElement.dataset.swoHeadless === "true" || this.targetElement.dataset.swoHeadless === ""),
      primaryColor: mergedOptions.primaryColor ?? this.targetElement.dataset.swoPrimaryColor,
    };

    // Initialize Modules
    this.ui = new UIManager(this.targetElement, this.options);
    this.consoleBridge = new ConsoleBridge();

    // Create UI first to generate elements
    this.ui.createUI();

    // Wiring up dependencies
    if (this.ui.elements.consoleOutput) {
        this.consoleBridge.setConsoleOutputElement(this.ui.elements.consoleOutput);
    }

    this.preview = new PreviewManager({
        getConsoleScript: () => this.consoleBridge.getIframeScript(),
        assetBaseURL: this.options.assetBaseURL
    });

    if (this.ui.elements.previewFrame && this.ui.elements.previewDevicesContainer) {
        this.preview.setElements(this.ui.elements.previewFrame, this.ui.elements.previewDevicesContainer);
    }

    this.editor = new EditorManager({
        code: this.options.code,
        storageKey: this.options.storageKey,
        resetOnLoad: this.options.resetOnLoad,
        container: this.ui.elements.codeEditorContainer!,
        onUpdate: (code) => {
            this.updatePreview();
        }
    });

    // Register this instance before running initialization steps so re-entrant
    // construction and auto-initialization observe consistent ownership.
    this.targetElement.swoInstance = this;

    try {
        this._initSteps();
    } catch (error) {
        this.destroy();
        throw error;
    }
  }

  isDestroyed(): boolean {
      return this._destroyed;
  }

  _initSteps() {
      // Init CodeMirror from the final selected document (no redundant
      // dispatch, save, or preview update during hydration).
      this.editor.init(this.editor.resolveInitialCode());

      // Init Event Listeners (Wiring)
      this._initEventListeners();

      // Initial Layout
      this.ui.initialLayout();
      this.resizePreviewDevice("100%", "100%");

      // Single initial preview update
      this.updatePreview(this.editor.getCode());
  }

  updatePreview(code?: string | null) {
      if (this._destroyed) return;
      this.editor.cancelPendingUpdate();
      const codeToUse = code !== undefined && code !== null ? code : this.editor.getCode();
      if (!this.options.preserveConsole) {
          this.consoleBridge.clear(false);
      }
      this.preview.update(codeToUse);
  }

  _initEventListeners() {
    const el = this.ui.elements;

    // Format
    if (el.codePrettierBtn) {
      el.codePrettierBtn.addEventListener("click", () => this.editor.formatCode());
    }

    // Console
    if (el.clearConsoleBtn) {
        el.clearConsoleBtn.addEventListener("click", () =>
            this.consoleBridge.clear(true)
        );
    }

    // Control Panel Buttons
    if (el.resizeDesktopBtn) el.resizeDesktopBtn.addEventListener("click", () => this.resizePreviewDevice("100%", "100%"));
    if (el.resizeTabletBtn) el.resizeTabletBtn.addEventListener("click", () => this.resizePreviewDevice("1070px", "100%"));
    if (el.resizeMobileBtn) el.resizeMobileBtn.addEventListener("click", () => this.resizePreviewDevice("390px", "844px"));

    if (el.refreshPreviewBtn) el.refreshPreviewBtn.addEventListener("click", () => this.updatePreview());
    if (el.openNewTabBtn) el.openNewTabBtn.addEventListener("click", () => this.preview.openNewTab(this.editor.getCode()));

    if (el.toggleCodeEditorBtn) el.toggleCodeEditorBtn.addEventListener("click", () => this.ui.toggleCodeEditor());
    if (el.toggleConsoleBtn) el.toggleConsoleBtn.addEventListener("click", () => this.ui.toggleConsole());

    // Only accept console messages from this instance's preview iframe.
    // Message shape is validated inside ConsoleBridge (F12), so malformed or
    // hostile payloads are dropped without throwing.
    this._boundWindowMessageHandler = (event: MessageEvent) => {
        const previewWindow = el.previewFrame?.contentWindow;
        if (!previewWindow || event.source !== previewWindow) return;
        this.consoleBridge.handleMessage(event.data);
    };
    window.addEventListener("message", this._boundWindowMessageHandler);
  }

  // Public Methods Proxy
  formatCode() { this.editor.formatCode(); }
  openPreviewInNewTab() { this.preview.openNewTab(this.editor.getCode()); }
  toggleCodeEditor() { this.ui.toggleCodeEditor(); }
  toggleConsole() { this.ui.toggleConsole(); }
  resizePreviewDevice(width: string, height: string) {
      if (this._destroyed) return;
      this.preview.resizeDevice(width, height);
      const el = this.ui.elements;
      this.ui.updateButtonActiveState(el.resizeDesktopBtn, width === "100%" && height === "100%");
      this.ui.updateButtonActiveState(el.resizeTabletBtn, width === "1070px" && height === "100%");
      this.ui.updateButtonActiveState(el.resizeMobileBtn, width === "390px" && height === "844px");
  }
  getCode() { return this.editor.getCode(); }
  setCode(code: string) { this.editor.setCode(code); this.updatePreview(); }
  destroy() {
      if (this._destroyed) return;
      this._destroyed = true;
      if (this._boundWindowMessageHandler) {
          window.removeEventListener("message", this._boundWindowMessageHandler);
          this._boundWindowMessageHandler = null;
      }
      this.ui.destroy();
      this.editor.destroy();
      this.preview.destroy();
      this.consoleBridge.destroy();
      // Clean up target
      this.targetElement.innerHTML = "";
      this.targetElement.classList.remove("swo-container");
      if (this.targetElement.swoInstance === this) {
          delete this.targetElement.swoInstance;
      }
  }
}

export default SWO;
